import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, AlertCircle, Download } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const [state, setState] = useState("verifying");
    const [inviteCode, setInviteCode] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!sessionId) {
            setState("error");
            return;
        }
        let tries = 0;
        let timer;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/support/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState("paid");
                    return;
                }
                tries += 1;
                if (tries >= 15) {
                    setState("pending");
                    return;
                }
                timer = setTimeout(poll, 2000);
            } catch {
                setState("error");
            }
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId]);

    useEffect(() => {
        if (state === "paid" && sessionId) {
            axios
                .get(`${API}/invite/link/${sessionId}`)
                .then((r) => setInviteCode(r.data.invite_code))
                .catch(() => {});
        }
    }, [state, sessionId]);

    const inviteUrl = inviteCode ? `${window.location.origin}/invite/${inviteCode}` : "";
    const copyInvite = async () => {
        try {
            await navigator.clipboard.writeText(inviteUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };

    const shareText = `I just became a Founding Family supporter of Ago GenApp — a private home for family memories across generations. Join me: ${inviteUrl || window.location.origin}`;
    const xShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(inviteUrl || window.location.origin)}`;

    const downloadShareCard = async () => {
        const canvas = document.createElement("canvas");
        canvas.width = 1200;
        canvas.height = 630;
        const ctx = canvas.getContext("2d");
        try {
            await document.fonts.load("700 84px 'Playfair Display'");
            await document.fonts.load("500 44px 'Playfair Display'");
        } catch {}
        ctx.fillStyle = "#FBF4EC";
        ctx.fillRect(0, 0, 1200, 630);
        const bar = ctx.createLinearGradient(0, 0, 1200, 0);
        bar.addColorStop(0, "#8E7BB5");
        bar.addColorStop(1, "#C98A94");
        ctx.fillStyle = bar;
        ctx.fillRect(0, 0, 1200, 14);
        ctx.textAlign = "center";
        ctx.fillStyle = "#1D2433";
        ctx.font = "500 46px 'Playfair Display', Georgia, serif";
        ctx.fillText("ago", 585, 150);
        ctx.fillStyle = "#C98A94";
        ctx.font = "34px Georgia, serif";
        ctx.fillText("✦", 668, 142);
        ctx.fillStyle = "#2C3347";
        ctx.font = "600 20px Arial, sans-serif";
        ctx.fillText("G E N A P P", 600, 196);
        ctx.fillStyle = "#1D2433";
        ctx.font = "700 84px 'Playfair Display', Georgia, serif";
        ctx.fillText("I’m a Founding Family.", 600, 336);
        ctx.fillStyle = "#2C3347";
        ctx.font = "400 30px Arial, sans-serif";
        ctx.fillText("Preserving our family’s legacy across generations — privately.", 600, 400);
        const pg = ctx.createLinearGradient(420, 0, 780, 0);
        pg.addColorStop(0, "#8E7BB5");
        pg.addColorStop(1, "#C98A94");
        ctx.fillStyle = pg;
        ctx.beginPath();
        ctx.roundRect(420, 448, 360, 66, 33);
        ctx.fill();
        ctx.fillStyle = "#FFFFFF";
        ctx.font = "600 26px Arial, sans-serif";
        ctx.fillText("agogenapp.com", 600, 491);
        ctx.fillStyle = "#8E7BB5";
        ctx.font = "400 20px Arial, sans-serif";
        ctx.fillText("Today’s moments. Tomorrow’s memories.", 600, 586);
        const a = document.createElement("a");
        a.download = "ago-founding-family.png";
        a.href = canvas.toDataURL("image/png");
        a.click();
    };

    const content = {
        verifying: {
            icon: <Loader2 size={36} className="animate-spin text-lavender" />,
            title: "Confirming your support…",
            body: "One moment while we confirm your contribution with Stripe.",
        },
        paid: {
            icon: <CheckCircle2 size={36} className="text-lavender" />,
            title: "Welcome to the Founding Circle.",
            body: "Your contribution is confirmed. You’ll receive an email with your tier details and early-access invitation when the beta opens. Thank you for helping families preserve their stories.",
        },
        pending: {
            icon: <AlertCircle size={36} className="text-peach" />,
            title: "Still processing.",
            body: "Your payment is taking a little longer to confirm. It will complete shortly — no need to pay again.",
        },
        error: {
            icon: <AlertCircle size={36} className="text-peach" />,
            title: "We couldn’t verify this session.",
            body: "If you completed a payment, it’s safe — contact us and we’ll sort it out right away.",
        },
    }[state];

    return (
        <main data-testid="payment-success-page" className="flex min-h-screen items-center justify-center bg-cream px-6 text-navy">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                className="w-full max-w-lg rounded-3xl border border-warmgray bg-ivory p-10 text-center shadow-xl shadow-navy/5"
            >
                <div className="flex justify-center">{content.icon}</div>
                <h1 data-testid="payment-status-title" className="mt-6 font-display text-2xl md:text-3xl">{content.title}</h1>
                <p data-testid="payment-status-text" className="mt-4 text-sm leading-relaxed text-navy">{content.body}</p>
                {state === "paid" && (
                    <div data-testid="share-card" className="mt-6 rounded-2xl border-2 border-warmgray bg-cream p-5">
                        <p className="text-left font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                            Spread the word
                        </p>
                        <div className="mt-3 overflow-hidden rounded-xl border border-warmgray">
                            <div className="h-1.5 bg-gradient-to-r from-lavender to-peach" />
                            <div className="bg-[#FBF4EC] px-4 py-6">
                                <p className="font-display text-lg text-navy">
                                    ago <span className="text-peach">✦</span>
                                </p>
                                <p className="mt-2 font-display text-xl font-bold leading-snug text-navy">
                                    I’m a Founding Family.
                                </p>
                                <p className="mt-1 text-xs text-[#2C3347]">agogenapp.com</p>
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <button
                                data-testid="download-card-button"
                                onClick={downloadShareCard}
                                className="btn-gradient flex items-center justify-center gap-2 rounded-full py-2.5 text-xs font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                            >
                                <Download size={14} /> Download Card
                            </button>
                            <a
                                data-testid="share-x-button"
                                href={xShareUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center rounded-full border-2 border-navy py-2.5 text-xs font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5"
                            >
                                Post on X
                            </a>
                            <a
                                data-testid="share-facebook-button"
                                href={fbShareUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center rounded-full border-2 border-navy py-2.5 text-xs font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5"
                            >
                                Share on Facebook
                            </a>
                        </div>
                    </div>
                )}
                {state === "paid" && inviteCode && (
                    <div data-testid="invite-share-card" className="mt-6 rounded-2xl border-2 border-warmgray bg-cream p-5 text-left">
                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                            Your Founding Supporter invite link
                        </p>
                        <p data-testid="invite-link-text" className="mt-2 break-all rounded-xl bg-[#FDFBF9] px-4 py-3 font-mono text-xs text-navy">
                            {inviteUrl}
                        </p>
                        <button
                            data-testid="copy-invite-button"
                            onClick={copyInvite}
                            className="btn-gradient mt-3 w-full rounded-full py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            {copied ? "Copied!" : "Copy Invite Link"}
                        </button>
                        <p className="mt-3 text-xs leading-relaxed text-navy">
                            Share it with family — they’ll join your shared legacy space with early access permissions.
                        </p>
                        <Link
                            data-testid="view-circle-link"
                            to={`/circle/${sessionId}`}
                            className="mt-4 inline-block rounded-full border-2 border-navy px-6 py-2.5 text-xs font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            View Your Family Circle
                        </Link>
                    </div>
                )}
                <Link
                    data-testid="back-home-link"
                    to="/"
                    className="btn-gradient mt-8 inline-block rounded-full px-8 py-3.5 text-sm font-medium text-white transition-transform duration-300 hover:-translate-y-0.5"
                >
                    Back to Ago GenApp
                </Link>
            </motion.div>
        </main>
    );
}
